import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { RelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAM";
import { SITUACAO_PARECER_MAP } from "@/contexts/fiscalizacao/relatorioAM/model/SITUACAO_PARECER_MAP";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { DeleteOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { App, Button, Col, Divider, List, Row, Typography } from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useState } from "react";

export function FormRemocaoParecerRAM({
  item,
}: {
  item: RelatorioAM["itens"][number];
}) {
  const { message, notification } = App.useApp();
  const {
    getPlanoAcaoByItemIdApi,
    getAnexoPlanoAcaoApi,
    removerParecerPlanoAcaoApi,
  } = useRelatorioAM();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: ultimoPlanoAcao, refetch } = useQuery({
    queryKey: ["PLANO_ACAO_BY_ITEM_ID", item.id],
    enabled: !!item.id,
    queryFn: async () => {
      const response = await getPlanoAcaoByItemIdApi(item.id!);

      const ultimoPlano = response.length > 0 ? response[0] : null;

      return ultimoPlano;
    },
  });

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoPlanoAcaoApi(nomeUpload);
      saveAs(anexoBlob, nomeUpload || "anexo");
    } catch (error) {
      message.error("Não foi possível abrir o anexo.");
    }
  };

  const handleSubmit = async () => {
    if (!ultimoPlanoAcao?.id) return;

    try {
      setIsSubmitting(true);
      message.loading({
        content: "Removendo parecer...",
        key: "saving",
      });

      await removerParecerPlanoAcaoApi(ultimoPlanoAcao?.id!);

      message.destroy("saving");
      setIsSubmitting(false);

      notification.success({
        message: "Parecer removido com sucesso na base de dados.",
      });

      refetch();
    } catch (error) {
      message.destroy("saving");
      setIsSubmitting(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível remover o parecer",
        description: messageError,
      });
    }
  };

  return (
    <div>
      <Divider>
        <Typography.Title level={5}>Parecer Plano de Ação</Typography.Title>
      </Divider>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Solução Geral:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.solucaoGeral || "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Prazo Ação Imediata:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.prazoAcaoImediata
              ? dayjs(ultimoPlanoAcao.prazoAcaoImediata).format("DD/MM/YYYY")
              : "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Medida Corretiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.medidaCorretiva || "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Prazo Ação Corretiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.prazoAcaoCorretiva
              ? dayjs(ultimoPlanoAcao.prazoAcaoCorretiva).format("DD/MM/YYYY")
              : "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Medida Preventiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.medidaPreventiva || "-"}
          </Typography.Paragraph>
        </Col>
        <Col xs={24} sm={12}>
          <Typography.Text strong type="secondary">
            Prazo Medida Preventiva:
          </Typography.Text>
          <Typography.Paragraph type="secondary">
            {ultimoPlanoAcao?.prazoAcaoPreventiva
              ? dayjs(ultimoPlanoAcao.prazoAcaoPreventiva).format("DD/MM/YYYY")
              : "-"}
          </Typography.Paragraph>
        </Col>

        {ultimoPlanoAcao?.anexos && ultimoPlanoAcao.anexos.length > 0 && (
          <Col xs={24}>
            <Typography.Text strong>Anexos:</Typography.Text>
            <List
              dataSource={ultimoPlanoAcao.anexos || []}
              renderItem={(anexo) => (
                <List.Item>
                  <Button
                    type="link"
                    onClick={() => handleOpenAnexo(anexo.nomeUpload)}
                  >
                    {anexo.nomeArquivo}
                  </Button>
                </List.Item>
              )}
            />
          </Col>
        )}

        {!ultimoPlanoAcao?.dataParecer && (
          <Col xs={24} style={{ textAlign: "center" }}>
            <Typography.Text type="secondary">
              Parecer ainda não foi registrado para este plano de ação.
            </Typography.Text>
          </Col>
        )}

        {ultimoPlanoAcao?.dataParecer && (
          <>
            <Divider orientation="left" orientationMargin={0}>
              <Typography.Title level={5}>
                Parecer -{" "}
                {dayjs(ultimoPlanoAcao.dataParecer).format("DD/MM/YYYY")}
              </Typography.Title>
            </Divider>

            <Col xs={24} sm={12}>
              <Typography.Text strong>Parecer:</Typography.Text>
              <Typography.Paragraph>
                {SITUACAO_PARECER_MAP[ultimoPlanoAcao.situacao]}
              </Typography.Paragraph>
            </Col>

            <Col xs={24} sm={12}>
              <Typography.Text strong>Registrado por:</Typography.Text>
              <Typography.Paragraph>
                {ultimoPlanoAcao.monitorInspetor?.nome}
              </Typography.Paragraph>
            </Col>

            <Col xs={24}>
              <Typography.Text strong>Descrição Parecer:</Typography.Text>
              <Typography.Paragraph>
                {ultimoPlanoAcao.descricaoParecer}
              </Typography.Paragraph>
            </Col>

            <Col xs={24}>
              <Row justify="center">
                <Col>
                  <Button
                    danger
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    icon={<DeleteOutlined />}
                    disabled={!ultimoPlanoAcao.dataParecer || isSubmitting}
                    onClick={handleSubmit}
                  >
                    Remover Parecer
                  </Button>
                </Col>
              </Row>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
}
